import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { MapPin, ArrowRight, Crosshair, Flag, SealCheck } from "@phosphor-icons/react";

export default function Courses() {
  const [courses, setCourses] = useState([]);
  useEffect(() => { api.get("/courses").then((r) => setCourses(r.data)); }, []);
  return (
    <div className="space-y-6">
      <div className="rise">
        <span className="label-eyebrow">Course directory</span>
        <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-1">Fiji golf courses</h1>
        <p className="text-[#0F2537]/70 mt-2 max-w-xl">Tap any course to view its scorecard and start a round.</p>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {courses.map((c, i) => {
          const tMapped = c.mapping?.tees ?? 0;
          const gMapped = c.mapping?.greens ?? 0;
          const tPct = Math.round((tMapped / 18) * 100);
          const gPct = Math.round((gMapped / 18) * 100);
          const fullyMapped = tMapped === 18 && gMapped === 18;
          return (
            <Link to={`/courses/${c.id}`} key={c.id} className="card-interactive overflow-hidden rise" style={{ animationDelay: `${i * 60}ms` }} data-testid={`course-card-${c.id}`}>
              <div className="aspect-[16/10] overflow-hidden bg-[#0F2537]/10 relative">
                {c.image_url && <img src={c.image_url} alt={c.name} className="w-full h-full object-cover" />}
                {fullyMapped && (
                  <span
                    data-testid={`course-fully-mapped-${c.id}`}
                    className="absolute top-2 right-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#1E5631] text-white text-[10px] font-bold uppercase tracking-wider shadow-md"
                    title="All 18 tees + 18 greens are hand-mapped on the satellite layer."
                  >
                    <SealCheck size={11} weight="fill" /> Fully Mapped
                  </span>
                )}
              </div>
              <div className="p-5">
                <div className="flex items-center gap-1 text-xs font-semibold text-[#0F2537]/60">
                  <MapPin size={12} weight="fill" /> {c.location}
                </div>
                <h3 className="font-heading font-bold text-lg mt-1">{c.name}</h3>
                <div className="flex items-center gap-4 mt-3 text-sm text-[#0F2537]/80">
                  <span><b>Par</b> {c.par}</span>
                  <span><b>CR</b> {c.course_rating}</span>
                  <span><b>Slope</b> {c.slope_rating}</span>
                </div>
                {/* GPS mapping progress — only renders when at least one hole is mapped */}
                {(tMapped + gMapped > 0) && (
                  <div className="mt-3 space-y-1.5" data-testid={`course-mapping-${c.id}`}>
                    <MappingBar icon={<Crosshair size={10} weight="bold" />} label="Tees"  count={tMapped} pct={tPct} color="#E05D3A" testid={`mapping-tees-${c.id}`} />
                    <MappingBar icon={<Flag size={10} weight="bold" />}      label="Greens" count={gMapped} pct={gPct} color="#1E5631" testid={`mapping-greens-${c.id}`} />
                  </div>
                )}
                <div className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-[#E05D3A]">
                  View course <ArrowRight size={14} weight="bold" />
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function MappingBar({ icon, label, count, pct, color, testid }) {
  return (
    <div className="flex items-center gap-2 text-[10px] font-bold text-[#0F2537]/70" data-testid={testid}>
      <span className="inline-flex items-center gap-1 w-14 shrink-0" style={{ color }}>{icon} {label}</span>
      <div className="flex-1 h-1.5 rounded-full bg-[#0F2537]/5 overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="tabular-nums w-12 text-right">{count}/18</span>
    </div>
  );
}
